import { supabase } from './supabaseClient';
import type { CMSSection } from '../types/db';

/**
 * Upload media via Edge Function.
 * Role validation is handled server-side for security.
 */
export async function uploadCMSMedia(file: File) {
  const formData = new FormData();
  formData.append('file', file);

  const { data, error } = await supabase.functions.invoke('upload-cms-media', {
    body: formData,
  });

  if (error) {
    /* console removed */
    throw error;
  }

  if (!data?.url) {
    throw new Error('Upload successful but no URL returned.');
  }

  return data.url as string;
}

/**
 * Update a CMS section specifically by its ID.
 */
export async function saveCMSSection(section: CMSSection) {
  if (section.id) {
    const { data, error } = await supabase
      .from('cms_sections')
      .update({
        content: section.content,
        is_active: section.is_active,
        position: section.position,
      })
      .eq('id', section.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } else {
    const { data, error } = await supabase
      .from('cms_sections')
      .insert({
        page_id: section.page_id,
        type: section.type,
        content: section.content,
        position: section.position,
        is_active: section.is_active,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }
}

/**
 * Fetch sections for a specific page.
 */
export async function fetchCMSSections(
  pageSlug: string = 'home',
  onlyActive: boolean = false
): Promise<CMSSection[]> {
  const { data: page } = await supabase
    .from('cms_pages')
    .select('id')
    .eq('slug', pageSlug)
    .maybeSingle();

  if (!page) return [];

  let query = supabase.from('cms_sections').select('*').eq('page_id', page.id);

  if (onlyActive) {
    query = query.eq('is_active', true);
  }

  const { data, error } = await query.order('position', { ascending: true });

  if (error) throw error;
  return data ?? [];
}
